import React from 'react';
import {
  EmployeeDashboardGrid,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';

export const AI_EMPLOYEE_INSIGHTS_STATUS = 'inactive';

export const AI_INSIGHT_PROVENANCE_FIELDS = Object.freeze([
  'sourceType',
  'sourceRecordId',
  'sourceDate',
  'generatedAt',
  'providerMetadata',
  'managerVerificationState'
]);

function SourceStatus({ label, status, detail }) {
  return (
    <li className="employee-ai-source" data-source-status={status.toLowerCase().replaceAll(' ', '-')}>
      <div>
        <h3>{label}</h3>
        <p>{detail}</p>
      </div>
      <span className="employee-ai-source__status">{status}</span>
    </li>
  );
}

function PlannedInsight({ title, description }) {
  return (
    <EmployeeWorkspaceSection title={title} description="Future governed capability">
      <EmployeeWorkspaceEmptyState
        title={`${title} is not active`}
        description={description}
        release="Planned"
      />
    </EmployeeWorkspaceSection>
  );
}

export function buildAiSourceEligibility({
  canReadProfile = false,
  canViewCoaching = false,
  canReadDocuments = false,
  attendanceEnabled = false,
  documentsEnabled = false
} = {}) {
  return [
    {
      label: 'Employee profile',
      status: canReadProfile ? 'Available' : 'Permission restricted',
      detail: canReadProfile
        ? 'The selected employee profile is available in this workspace.'
        : 'Profile detail is not eligible for this viewer.'
    },
    {
      label: 'Attendance',
      status: attendanceEnabled && canReadProfile ? 'Available' : (attendanceEnabled ? 'Permission restricted' : 'Unavailable'),
      detail: attendanceEnabled && canReadProfile
        ? 'Employee-profile-scoped attendance records are available; no manager or location aggregate is substituted.'
        : (attendanceEnabled ? 'Attendance is not eligible without profile access.' : 'The attendance module is not enabled.')
    },
    {
      label: 'Coaching',
      status: canViewCoaching ? 'Available' : 'Permission restricted',
      detail: canViewCoaching
        ? 'Authorized employee coaching records are eligible for future use.'
        : 'Coaching content is excluded for this viewer.'
    },
    {
      label: 'Certifications',
      status: documentsEnabled && canReadDocuments ? 'Available' : (documentsEnabled ? 'Permission restricted' : 'Unavailable'),
      detail: documentsEnabled && canReadDocuments
        ? 'Authorized certification documents are available.'
        : (documentsEnabled ? 'Certification documents are excluded for this viewer.' : 'The documents module is not enabled.')
    },
    {
      label: 'Career aspirations',
      status: canReadProfile ? 'Available' : 'Permission restricted',
      detail: canReadProfile
        ? 'The employee profile can contain a career-aspirations record; no advice is generated from it.'
        : 'Career profile detail is not eligible for this viewer.'
    },
    {
      label: 'Training history',
      status: 'Not modeled',
      detail: 'No LMS or employee training-history source exists in the current data model.'
    },
    {
      label: 'Employee goals',
      status: 'Not modeled',
      detail: 'Current goals belong to locations and manager allocations, not employee profiles.'
    },
    {
      label: 'Employee operational KPIs',
      status: 'Not modeled',
      detail: 'Current operational metrics are manager- or location-scoped and are excluded from employee insights.'
    }
  ];
}

export default function EmployeeAiInsightsWorkspace({
  canReadProfile = false,
  canViewCoaching = false,
  canReadDocuments = false,
  attendanceEnabled = false,
  documentsEnabled = false
}) {
  const sources = buildAiSourceEligibility({
    canReadProfile,
    canViewCoaching,
    canReadDocuments,
    attendanceEnabled,
    documentsEnabled
  });

  return (
    <div className="employee-ai-workspace" data-testid="employee-ai-insights-workspace">
      <EmployeeWorkspaceSection title="AI overview" description="Current availability and operating boundary">
        <div className="employee-ai-notice" role="status" aria-live="polite">
          <strong>AI employee insights are not currently active.</strong>
          <p>No AI processing is active and no employee data is sent to an AI provider. Future insights may use only authorized, tenant-scoped VizionOps employee data after governance approval.</p>
        </div>
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Evidence available" description="Real source availability for this viewer">
        <ul className="employee-ai-sources" aria-label="AI source eligibility">
          {sources.map(source => <SourceStatus key={source.label} {...source} />)}
        </ul>
      </EmployeeWorkspaceSection>

      <PlannedInsight title="Employee summary" description="No summary is generated or stored while the approved AI runtime and provenance controls are absent." />
      <EmployeeDashboardGrid>
        <PlannedInsight title="Strengths" description="No strengths are inferred from profile, attendance, coaching, certification, or career records." />
        <PlannedInsight title="Attention areas" description="No concerns, risk labels, predictions, or disciplinary signals are generated." />
        <PlannedInsight title="Development recommendations" description="No career advice or manager action is generated." />
        <PlannedInsight title="Promotion preparation" description="No readiness score, verdict, or promotion decision is produced." />
        <PlannedInsight title="Recent coaching synthesis" description="No coaching content is summarized. Any future synthesis must preserve the coaching permission boundary." />
        <PlannedInsight title="Operational insights" description="Manager and location aggregates are not employee evidence. This remains unavailable until a real employee-level operational model exists." />
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="AI governance and sources" description="Controls required before activation">
        <dl className="employee-ai-governance">
          <div><dt>AI status</dt><dd>Inactive — governance preparation only</dd></div>
          <div><dt>Eligible source categories</dt><dd>Only sources marked Available above, subject to server authorization at generation time</dd></div>
          <div><dt>Excluded categories</dt><dd>Pilot-prohibited identity, financial, health, legal, protected-class, family, belief, political, genetic, and background-check data</dd></div>
          <div><dt>Operational KPI boundary</dt><dd>Manager and location aggregates are excluded</dd></div>
          <div><dt>Generation record</dt><dd>None — no output, prompt, model metadata, or generated timestamp exists</dd></div>
          <div><dt>Human review</dt><dd>Future assistance must be source-linked and verified by an authorized manager; it must not make employment decisions</dd></div>
        </dl>
      </EmployeeWorkspaceSection>
    </div>
  );
}
